import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Upload, Download, Trash2, FileText } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer, Field, formatDate } from "@/components/admin/ui";
import { CREDENTIAL_TYPE_META, CREDENTIAL_STATUS_STYLES, formatFileSize, scopeReferenceLabel } from "@/lib/credentials";

export const CredentialDetailDrawer = ({ credentialId, meta, onClose, onChanged, onEdit }) => {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState(null);
  const [busyDocId, setBusyDocId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/org/credentials/${credentialId}`, { withCredentials: true });
      setData(res);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API, credentialId]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setError("");
    setUploadProgress(0);
    try {
      await axios.post(`${API}/org/credentials/${credentialId}/documents`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => setUploadProgress(Math.round((evt.loaded * 100) / (evt.total || 1))),
      });
      await load();
      onChanged?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't upload this document.");
    } finally {
      setUploadProgress(null);
    }
  };

  const downloadDocument = async (doc, download) => {
    const url = `${API}/org/credentials/${credentialId}/documents/${doc.document_id}${download ? "?download=true" : ""}`;
    window.open(url, "_blank");
  };

  const removeDocument = async (doc) => {
    setBusyDocId(doc.document_id);
    try {
      await axios.delete(`${API}/org/credentials/${credentialId}/documents/${doc.document_id}`, { withCredentials: true });
      setConfirmDeleteDocId(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyDocId(null);
    }
  };

  const credential = data?.credential;
  const typeMeta = credential ? CREDENTIAL_TYPE_META[credential.credential_type] : null;
  const canManage = !!credential?.can_manage;

  return (
    <DetailDrawer open={!!credentialId} onClose={onClose} title={credential?.credential_name || "Credential"} subtitle={typeMeta?.singular} testId="credential-detail-drawer">
      {!credential ? (
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="credential-detail-loading">Loading…</p>
      ) : (
        <div className="space-y-6">
          {canManage && (
            <button type="button" onClick={() => onEdit(credential)} data-testid="credential-detail-edit-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
              <Pencil size={12} /> Edit Credential
            </button>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Credential Type" value={typeMeta?.singular} testId="credential-detail-type" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Status</p>
              <div className="mt-1">
                <span data-testid="credential-detail-status" className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${CREDENTIAL_STATUS_STYLES[credential.status] || ""}`}>{credential.status}</span>
              </div>
            </div>
            <Field label="Credential Number" value={credential.credential_number} testId="credential-detail-number" />
            <Field label="Issued By" value={credential.issuer} testId="credential-detail-issuer" />
            <Field label="Holder" value={`${credential.holder_type === "organization" ? "Organization" : "Person"} · ${credential.holder_name || "—"}`} testId="credential-detail-holder" />
            <Field label="Operational Scope" value={scopeReferenceLabel(credential, meta)} testId="credential-detail-scope" />
            <Field label="Issue Date" value={formatDate(credential.issue_date)} testId="credential-detail-issue-date" />
            <Field label="Expiration Date" value={credential.does_not_expire ? "Does not expire" : formatDate(credential.expiration_date)} testId="credential-detail-expiration-date" />
          </div>

          {credential.description && <Field label="Description" value={credential.description} testId="credential-detail-description" />}
          {credential.notes && <Field label="Notes" value={credential.notes} testId="credential-detail-notes" />}

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Documents</p>

            {error && <p className="mt-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="credential-detail-error">{error}</p>}

            <div className="mt-2 space-y-2" data-testid="credential-detail-documents-list">
              {(data.documents || []).length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="credential-detail-documents-empty">No documents uploaded yet.</p>
              ) : (
                data.documents.map((doc) => (
                  <div key={doc.document_id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 dark:border-white/10" data-testid={`credential-detail-document-${doc.document_id}`}>
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText size={14} className="shrink-0 text-slate-400" />
                      <span className="truncate text-xs font-semibold text-slate-700 dark:text-slate-200">{doc.original_filename}</span>
                      <span className="shrink-0 text-xs text-slate-400">{formatFileSize(doc.size_bytes)}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button type="button" onClick={() => downloadDocument(doc, false)} data-testid={`credential-detail-document-preview-${doc.document_id}`} className="text-slate-400 hover:text-slate-700 dark:hover:text-white" title="Preview">
                        <FileText size={14} />
                      </button>
                      <button type="button" onClick={() => downloadDocument(doc, true)} data-testid={`credential-detail-document-download-${doc.document_id}`} className="text-slate-400 hover:text-slate-700 dark:hover:text-white" title="Download">
                        <Download size={14} />
                      </button>
                      {canManage && (
                        confirmDeleteDocId === doc.document_id ? (
                          <div className="flex items-center gap-1" data-testid={`credential-detail-document-confirm-${doc.document_id}`}>
                            <button type="button" onClick={() => removeDocument(doc)} disabled={busyDocId === doc.document_id} data-testid={`credential-detail-document-confirm-yes-${doc.document_id}`} className="rounded bg-red-600 px-2 py-1 text-[11px] font-bold text-white disabled:opacity-50">Delete?</button>
                            <button type="button" onClick={() => setConfirmDeleteDocId(null)} data-testid={`credential-detail-document-confirm-cancel-${doc.document_id}`} className="rounded border border-slate-200 px-2 py-1 text-[11px] font-bold text-slate-500 dark:border-white/15">Cancel</button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setConfirmDeleteDocId(doc.document_id)} data-testid={`credential-detail-document-delete-${doc.document_id}`} className="text-red-500 hover:text-red-700" title="Delete">
                            <Trash2 size={14} />
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {canManage && (
              <div className="mt-3">
                <label htmlFor="credential-detail-file-input" className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10" data-testid="credential-detail-upload-btn">
                  <Upload size={13} /> Upload another document
                </label>
                <input id="credential-detail-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} data-testid="credential-detail-file-input" />
                {uploadProgress !== null && (
                  <div className="mt-2" data-testid="credential-detail-upload-progress">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                      <div className="h-full rounded-full bg-[#0052FF] transition-[width] duration-150 ease-out" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </DetailDrawer>
  );
};
